import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { useEffect, type Ref } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';
import { useViewingAs } from '@/auth/useViewingAs';

/**
 * Says, on every page, that staff are looking at an organization read-only,
 * and offers the way out.
 *
 * A status region, so its arrival is announced when the view begins. It sits
 * inside the fixed top bar, which is the one place that is both after the skip
 * link and before the navigation for somebody moving by keyboard. It is the
 * top row, above the bar itself, so everything below it reads as the
 * organization's own portal.
 */
export function ViewingBanner({ ref }: { ref?: Ref<HTMLDivElement> }) {
  const { viewing, exit, leaving, leave } = useViewingAs();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  /*
   * Leave in two steps: ask to leave and go to the staff account page, and end
   * the view once a staff page is the one on screen.
   *
   * Ending it any earlier leaves the organization section still showing with
   * no view to allow it, and the route guard sends staff to the no-access page
   * - the router applies a navigation a moment after it is asked for. The
   * request to leave is held by the viewing state rather than on the address,
   * so View portal clears it at once and nothing left over can end a new view.
   */
  const onStaffPage = pathname.startsWith('/staff/');
  useEffect(() => {
    if (leaving && onStaffPage) exit();
  }, [leaving, onStaffPage, exit]);

  if (!viewing) return null;

  function goBack() {
    leave();
    void navigate(ROUTES.staff.organization(viewing!.id));
  }

  return (
    <Box ref={ref}>
      <Alert
        severity="info"
        role="status"
        square
        action={
          <Button size="sm" variant="secondary" onClick={goBack}>
            Exit
          </Button>
        }
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          // On a narrow screen the words wrap and the button drops beneath
          // them, rather than pushing the page sideways.
          flexWrap: 'wrap',
          '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' },
          '& .MuiAlert-action': { ml: { xs: 0, sm: 'auto' }, pl: { xs: 0, sm: 2 } },
        }}
      >
        Viewing <strong>{viewing.name}</strong> as Activist staff - read-only
      </Alert>
    </Box>
  );
}
